import { action, makeObservable, observable, runInAction } from 'mobx';
import { AuthenticationData, IAuthService } from 'src/types/AuthService';
import { serviceLoader } from 'src/stores/ServiceLoader';
import { IInvitationService } from 'src/types/InvitationService';
import { IUpdateUserData, IUser } from 'src/types/User';
import { IPasswordService } from 'src/types/PasswordService';
import { IUserService } from 'src/types/UserService';
import { IReconfirmAccountService } from 'src/types/ReconfirmAccountService';

export interface IAuthStoreState {
  user?: IUser;
  authenticationToken: string;
  pending2faToken: string | null;
}

export class AuthStore {
  state: IAuthStoreState = {
    user: undefined,
    authenticationToken: '',
    pending2faToken: null,
  };

  authService: IAuthService;
  invitationService: IInvitationService;
  passwordService: IPasswordService;
  userService: IUserService;
  reconfirmAccountService: IReconfirmAccountService;

  constructor() {
    makeObservable(this, {
      state: observable,
      login: action.bound,
      logout: action.bound,
      init: action.bound,
      updateUser: action.bound,
      setupTwoFactor: action.bound,
      confirmTwoFactor: action.bound,
      verifyOTP: action.bound,
      clearPending2faToken: action.bound,
    });

    this.authService = serviceLoader.load('authService');
    this.invitationService = serviceLoader.load('invitationService');
    this.passwordService = serviceLoader.load('passwordService');
    this.userService = serviceLoader.load('userService');
    this.reconfirmAccountService = serviceLoader.load(
      'reconfirmAccountService'
    );
    this.init();
  }

  init(): void {
    const authData = this.authService.getAuthenticationData();

    if (authData) {
      this.state.user = authData.user;
      this.state.authenticationToken = authData.authenticationToken;
    }
  }

  async login(email: string, password: string): Promise<'success' | 'requires_otp'> {
    const response = await this.authService.login(email, password);

    // Check if 2FA is required
    if ('requires_otp' in response && response.requires_otp) {
      runInAction(() => {
        this.state.pending2faToken = response.pending_2fa_token;
      });
      return 'requires_otp';
    }

    // Normal login flow
    const authData = response as AuthenticationData;
    runInAction(() => {
      this.state.authenticationToken = authData.authenticationToken;
      this.state.user = authData.user;
      this.state.pending2faToken = null;
    });
    return 'success';
  }

  async logout(): Promise<void> {
    await this.authService.logout();

    runInAction(() => {
      this.state.authenticationToken = '';
      this.state.user = undefined;
    });
  }

  async acceptInvitation(
    invitationToken: string,
    password: string
  ): Promise<void> {
    await this.invitationService.acceptInvitation(invitationToken, password);
  }

  async reconfirmAccount(confirmationToken: string): Promise<void> {
    await this.reconfirmAccountService.reconfirmAccount(confirmationToken);
  }

  async forgotPassword(email: string): Promise<void> {
    await this.passwordService.forgotPassword(email);
  }

  async resetPassword(
    resetPasswordToken: string,
    password: string
  ): Promise<void> {
    await this.passwordService.resetPassword(resetPasswordToken, password);
  }

  async updateUser(user: IUpdateUserData): Promise<void> {
    await this.userService.updateUser(user);
    const currentUser = this.state.user;

    runInAction(() => {
      if (!currentUser) {
        throw new Error('User is not logged in');
      }

      currentUser.firstName = user.firstName;
      currentUser.lastName = user.lastName;
      currentUser.email = user.email;
    });
  }

  async setupTwoFactor() {
    // Pass the token from state to avoid cookie reading issues right after login
    return await this.authService.setupTwoFactor(this.state.authenticationToken);
  }

  async confirmTwoFactor(otpCode: string): Promise<void> {
    // Pass the token from state to avoid cookie reading issues
    await this.authService.confirmTwoFactor(otpCode, this.state.authenticationToken);
  }

  async verifyOTP(otpCode: string): Promise<void> {
    if (!this.state.pending2faToken) {
      throw new Error('No pending 2FA token');
    }

    const { authenticationToken, user } = await this.authService.verifyOTP(
      otpCode,
      this.state.pending2faToken
    );

    runInAction(() => {
      this.state.authenticationToken = authenticationToken;
      this.state.user = user;
      this.state.pending2faToken = null;
    });
  }

  clearPending2faToken(): void {
    this.state.pending2faToken = null;
  }
}
